/**
 * CORE-1116 — кому и куда предлагать приложение.
 *
 * Правила отделены от разметки, потому что проверить их в браузере я не могу, а
 * ошибиться здесь легче всего: строка user-agent у iPadOS притворяется
 * макинтошем, а у десктопного Chrome с включённой эмуляцией — телефоном.
 *
 * Ссылки на сторы — из комментария Андрея в задаче (26.08.2026).
 */

export type MobileStore = 'ios' | 'android';

export const STORE_URLS: Record<MobileStore, string> = {
  ios: 'https://apps.apple.com/ru/app/agapella/id6786169744',
  android: 'https://play.google.com/store/apps/details?id=com.agapella.app',
};

export const STORE_LABEL: Record<MobileStore, string> = {
  ios: 'App Store',
  android: 'Google Play',
};

/**
 * Какой стор предлагать по строке user-agent. `null` — не предлагать вовсе:
 * десктоп, бот или что-то неопознанное. Молчать в незнакомом случае правильнее,
 * чем звать человека в стор, которого у него нет.
 *
 * @param maxTouchPoints нужен ровно для iPad: с iPadOS 13 он пишется в
 *   user-agent макинтошем, и отличает его только наличие тача. У настоящего
 *   Mac это 0 — там баннера быть не должно.
 */
export function detectMobileStore(userAgent: string | null | undefined, maxTouchPoints = 0): MobileStore | null {
  if (!userAgent) return null;
  const ua = userAgent.toLowerCase();

  // Ботам предлагать нечего, а в выдаче поиска такой баннер только мешает.
  if (/bot|crawler|spider|crawling|headless/.test(ua)) return null;

  if (/android/.test(ua)) {
    // «Android» встречается и в десктопных эмуляциях, но там же есть `mobile`
    // либо планшетная строка без него — обе нам подходят, а вот Windows с
    // подстрокой Android не бывает.
    return /windows|macintosh; (?!.*mobile)/.test(ua) ? null : 'android';
  }

  if (/iphone|ipod|ipad/.test(ua)) return 'ios';

  // iPadOS 13+ представляется макинтошем; тач выдаёт в нём планшет.
  if (/macintosh/.test(ua) && maxTouchPoints > 1) return 'ios';

  return null;
}

/**
 * CORE-1723 — настоящий Safari на iPhone или iPad: браузер, который сам
 * показывает Smart App Banner (`apple-itunes-app` в index.html) — «Открыть»
 * при установленном приложении, «Загрузить» без него. Наш баннер там — второй.
 *
 * У Safari есть `Version/… Safari/`. У Chrome, Firefox, Edge и прочих на iOS —
 * свой токен, у встроенных браузеров приложений либо нет `Version/`, либо есть
 * имя приложения; системного баннера у них нет, и наш остаётся. Неопознанное —
 * «не Safari»: лишний наш баннер лучше, чем ни одного предложения.
 */
export function isIosSafari(userAgent: string | null | undefined, maxTouchPoints = 0): boolean {
  if (!userAgent || detectMobileStore(userAgent, maxTouchPoints) !== 'ios') return false;
  const ua = userAgent.toLowerCase();
  if (!/version\/[\d.]+.*safari\//.test(ua)) return false;
  return !/crios|fxios|edgios|opios|opt\/|yabrowser|gsa\/|ddg\/|duckduckgo|fban|fbav|instagram|line\/|telegram|vkclient|micromessenger/.test(ua);
}

/**
 * CORE-1723 — пакет приложения в Google Play: тот же, что в `STORE_URLS.android`
 * и в `related_applications` у `public/manifest.json` (тест сверяет все три).
 */
export const ANDROID_PACKAGE = 'com.agapella.app';

type RelatedApp = { platform?: string; id?: string };
type RelatedAppsNavigator = { getInstalledRelatedApps?: () => Promise<RelatedApp[]> };

/**
 * CORE-1723 — стоит ли на телефоне наше Android-приложение. Если да, на кнопке
 * баннера «Открыть», а не «Скачать».
 *
 * Сайт может узнать это только в Chrome на Android (`getInstalledRelatedApps`),
 * и только если само приложение объявило сайт у себя в манифесте
 * (`asset_statements`): без этого Chrome отвечает пустым списком. У Safari,
 * Firefox и остальных браузеров такого способа нет вовсе. Любое «не знаю» —
 * false: ошибиться в сторону «Скачать» безопаснее, чем звать открыть то, чего нет.
 */
export async function isAndroidAppInstalled(nav: object | undefined): Promise<boolean> {
  const getApps = (nav as RelatedAppsNavigator | undefined)?.getInstalledRelatedApps;
  if (typeof getApps !== 'function') return false;
  try {
    // Метод навигатора: вызванный без своего `this`, он бросает Illegal invocation.
    const apps = await getApps.call(nav);
    return Array.isArray(apps) && apps.some((app) => app.platform === 'play' && app.id === ANDROID_PACKAGE);
  } catch {
    return false;
  }
}

/**
 * Ссылка «Открыть в приложении» для Chrome на Android: тот же экран, что открыт
 * на сайте, по своей схеме `agapella://` — она открывает приложение всегда, без
 * проверки домена (так же зовёт «Открыть в приложении» после регистрации).
 * Экрана, которого в приложении нет, оно не найдёт и откроет главную.
 *
 * @param pathAndQuery путь с параметрами, без хэша: `#` в интент-ссылке занят
 *   её собственной частью. Если приложение успели удалить, Chrome по
 *   `browser_fallback_url` сам уводит в Google Play.
 */
export function androidOpenInAppUrl(pathAndQuery: string): string {
  const target = pathAndQuery.replace(/^\/+/, '');
  return `intent://${target}#Intent;scheme=agapella;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(STORE_URLS.android)};end`;
}

/**
 * CORE-1965 — у Android два магазина: Google Play и RuStore. Приложение одно
 * и то же, пакет `com.agapella.app` (в RuStore уходит та же сборка —
 * docs/mobile-release/rustore.md), поэтому обе ссылки — карточки этого
 * пакета, а не главная магазина и не APK.
 *
 * Карточка RuStore по справке магазина (rustore-deeplinks): при установленном
 * RuStore web-адрес открывает сам магазин, без него — страницу витрины.
 * Отдельная схема `rustore://` не нужна. Проверено 03.11.2026: карточка
 * Agapella 1.0.2 на месте.
 */
export type AndroidMarket = 'play' | 'rustore';

export const ANDROID_MARKET_URLS: Record<AndroidMarket, string> = {
  play: STORE_URLS.android,
  rustore: `https://www.rustore.ru/catalog/app/${ANDROID_PACKAGE}`,
};

export const ANDROID_MARKET_LABEL: Record<AndroidMarket, string> = {
  play: STORE_LABEL.android,
  rustore: 'RuStore',
};
